import InvoiceIcon from 'components/ReusableComponents/Icons/Invoice'
import InvoiceRecipientsDialog from 'components/ReusableComponents/InvoiceRecipientsDialog'
import {
  userPositionNames,
  userPositions
} from 'features/ManageUserDialog/userTitleTypes'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications/dist/ToastProvider'
import billingApi from 'services/api/billing'

import ConfirmationDialog from '../ConfirmationDialog/index'
import LoadingIndicator from '../LoadingIndicator'
import Dropdown from '../ManageHotel/Dropdown/index'
import styles from './BillingContactsDialog.module.scss'

const BillingContactsDialog = ({ handleHideDialog, handleConfirm, isSendingInvoice }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [billingContacts, setBillingContacts] = useState()
  const [contactsData, setContactsData] = useState([])

  const [isInvoiceRecipientDialogVisible, setIsInvoiceRecipientDialogVisible] =
    useState(false)

  const { addToast } = useToasts()

  const [fetchIndex, setFetchIndex] = useState(0)

  function handleSaveInvoiceRecipients() {
    setIsLoading(true)
    setFetchIndex(fetchIndex + 1)
    setIsInvoiceRecipientDialogVisible(false)
  }

  useEffect(() => {
    setIsLoading(true)
    billingApi.billingContacts().then(response => {
      setBillingContacts(response)

      if (response && !response.find(c => c.isInvoiceRecipient)) {
        addToast('Add at least one contact to send invoice', {
          appearance: 'info',
          autoDismiss: true
        })
        setIsInvoiceRecipientDialogVisible(true)
      }

      setIsLoading(false)
    })
  }, [addToast, fetchIndex])

  useEffect(() => {
    if (!isLoading && billingContacts?.length) {
      setContactsData(
        billingContacts.map(c => ({ ...c, sendEmail: c.isInvoiceRecipient }))
      )
    }
  }, [billingContacts, isLoading])

  const [isButtonDisabled, setButtonDisabled] = useState(true)

  function handleChange(key, userId, isChecked) {
    setContactsData(() =>
      contactsData.map(c => (c.userId === userId ? { ...c, [key]: isChecked } : c))
    )
  }

  function handleRowChange(key, isChecked) {
    setContactsData(contactsData.map(c => ({ ...c, [key]: isChecked })))
  }

  const [sendReason, setSendReason] = useState()

  useEffect(() => {
    setButtonDisabled(!contactsData.some(p => p.sendText || p.sendEmail) || !sendReason)
  }, [contactsData, sendReason])

  if (isInvoiceRecipientDialogVisible) {
    return (
      <InvoiceRecipientsDialog
        onClose={handleHideDialog}
        onSave={handleSaveInvoiceRecipients}
      />
    )
  }

  return (
    <ConfirmationDialog
      title="Select contacts and methods"
      positiveMessage={isSendingInvoice ? 'Sending...' : 'Send Invoice'}
      theme="blacklist"
      maxWidth={600}
      onConfirm={() =>
        handleConfirm({
          recipients: contactsData.map(c => ({
            userId: c.userId,
            sendEmail: c.sendEmail,
            sendText: false
          })),
          sendReason
        })
      }
      onClose={handleHideDialog}
      isButtonDisabled={isLoading || isButtonDisabled || isSendingInvoice}
    >
      {isLoading ? (
        <div style={{ height: 200 }}>
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <Table
            textChecked={contactsData.every(c => c.sendText)}
            emailChecked={contactsData.every(c => c.sendEmail)}
            handleTextChange={e => handleRowChange('sendText', e.target.checked)}
            handleEmailChange={e => handleRowChange('sendEmail', e.target.checked)}
          >
            {contactsData
              .sort((a, b) => Number(b.isInvoiceRecipient) - Number(a.isInvoiceRecipient))
              .map(c => (
                <Row
                  key={c.userId}
                  id={c.userId}
                  name={c.fullName}
                  isGroupContact={c.isGroupBillingManager}
                  hotel={c.hotelName}
                  group={c.merchantName}
                  position={c.position}
                  handleChangeEmail={e =>
                    handleChange('sendEmail', c.userId, e.target.checked)
                  }
                  sendEmail={c.sendEmail}
                  isInvoiceRecipient={c.isInvoiceRecipient}
                  sendText={c.sendText}
                />
              ))}
          </Table>
          {/*
            ForReconciliation ("For reconciliation") is not listed: the payment reconciliation mail
            template is obsolete. Values must match api-dashboard InvoiceSendReason
            (ForPayment was renamed to ForPaymentFinalized).
          */}
          <Dropdown
            className={styles.emailType}
            defaultVal="Select message type"
            value={sendReason}
            title="select message type"
            onChange={e => setSendReason(e.target.value)}
            options={{
              ForPaymentFinalized: 'For payment',
              ForPaymentLate: 'Payment late',
              ForPaymentLastWarning: 'Payment really late'
            }}
          />
        </>
      )}
    </ConfirmationDialog>
  )
}

const Table = ({ children, emailChecked, handleEmailChange }) => (
  <div className={styles.table}>
    <div className={styles.tableTitle}>
      <div className={styles.tableTitleContact}>Contact</div>
      <div className={styles.tableTitleEmail}>
        <label>
          <span>Email</span>
          <input type="checkbox" checked={emailChecked} onChange={handleEmailChange} />
        </label>
      </div>
    </div>
    <div className={styles.tableContent}>{children}</div>
  </div>
)

const Row = ({
  id,
  name,
  hotel,
  isGroupContact,
  position,
  group,
  sendEmail,
  handleChangeEmail,
  isInvoiceRecipient
}) => (
  <div className={styles.row}>
    <div className={styles.colName}>
      {isInvoiceRecipient && <InvoiceIcon className={styles.invoiceIcon} />}
      <div>
        <div className={styles.fullName}>{name}</div>
        <div className={isGroupContact ? styles.roleTitle : styles.hotelTitle}>
          {isGroupContact ? `${group} billing contact` : hotel}
        </div>
      </div>
      {position && position !== userPositions.Unknown && (
        <span className={styles.positionPill}>{userPositionNames[position]}</span>
      )}
    </div>
    <div className={styles.colEmail}>
      <label>
        <input
          type="checkbox"
          title={`email${id}`}
          checked={sendEmail}
          onChange={handleChangeEmail}
        />
      </label>
    </div>
  </div>
)

export default BillingContactsDialog
